/**
 * Scroll Animation Controller
 *
 * Unified GSAP ScrollTrigger-based controller for scroll-triggered animations.
 * Works with data-animate and data-animate-stagger attributes.
 *
 * Usage:
 *   <div data-animate="fade-up">Animates on scroll</div>
 *   <div data-animate-stagger>Children animate sequentially</div>
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

/**
 * Scroll Animation Controller Class
 */
export class ScrollAnimationController {
  constructor() {
    this.initialized = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.triggers = [];
  }

  /**
   * Initialize the scroll animation controller
   */
  init() {
    // Don't initialize if reduced motion is preferred
    if (this.prefersReducedMotion) {
      console.log('[ScrollController] Skipping - prefers-reduced-motion enabled');
      this.showAllElements();
      return;
    }

    // Don't double-initialize
    if (this.initialized) {
      console.log('[ScrollController] Already initialized');
      return;
    }

    this.initAnimateElements();
    this.initStaggerElements();

    this.initialized = true;
    console.log('[ScrollController] Initialized successfully');
  }

  /**
   * Initialize single element animations (data-animate)
   */
  initAnimateElements() {
    const elements = document.querySelectorAll('[data-animate]');

    elements.forEach((element) => {
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: 'top 75%',  // Trigger when top of element reaches 75% of viewport
        once: true,         // Only trigger once - content stays visible after reveal
        onEnter: () => {
          element.classList.add('is-visible');
        },
      });

      this.triggers.push(trigger);
    });

    console.log(`[ScrollController] Initialized ${elements.length} animate elements`);
  }

  /**
   * Initialize stagger container animations (data-animate-stagger)
   */
  initStaggerElements() {
    const containers = document.querySelectorAll('[data-animate-stagger]');

    containers.forEach((container) => {
      const children = container.children;

      // Set stagger index CSS variable on each child
      Array.from(children).forEach((child, index) => {
        child.style.setProperty('--stagger-index', index);
      });

      const trigger = ScrollTrigger.create({
        trigger: container,
        start: 'top 75%',  // Match single element trigger point
        once: true,         // Only trigger once - content stays visible after reveal
        onEnter: () => {
          container.classList.add('is-visible');
        },
      });

      this.triggers.push(trigger);
    });

    console.log(`[ScrollController] Initialized ${containers.length} stagger containers`);
  }

  /**
   * Show all elements immediately (for reduced motion)
   */
  showAllElements() {
    document.querySelectorAll('[data-animate]').forEach((el) => {
      el.classList.add('is-visible');
    });

    document.querySelectorAll('[data-animate-stagger]').forEach((el) => {
      el.classList.add('is-visible');
    });
  }

  /**
   * Refresh ScrollTrigger (call after DOM changes)
   */
  refresh() {
    ScrollTrigger.refresh();
  }

  /**
   * Destroy all triggers and clean up
   */
  destroy() {
    this.triggers.forEach((trigger) => trigger.kill());
    this.triggers = [];
    this.initialized = false;
    console.log('[ScrollController] Destroyed');
  }
}

// Create singleton instance
let controllerInstance = null;

/**
 * Initialize the scroll animation controller
 * @returns {ScrollAnimationController}
 */
export function initScrollAnimations() {
  if (!controllerInstance) {
    controllerInstance = new ScrollAnimationController();
  }
  controllerInstance.init();
  return controllerInstance;
}

/**
 * Get the controller instance
 * @returns {ScrollAnimationController|null}
 */
export function getScrollController() {
  return controllerInstance;
}

/**
 * Refresh scroll animations (call after DOM changes)
 */
export function refreshScrollAnimations() {
  if (controllerInstance) {
    controllerInstance.refresh();
  }
}

/**
 * Destroy scroll animations
 */
export function destroyScrollAnimations() {
  if (controllerInstance) {
    controllerInstance.destroy();
    controllerInstance = null;
  }
}

export default {
  init: initScrollAnimations,
  get: getScrollController,
  refresh: refreshScrollAnimations,
  destroy: destroyScrollAnimations,
};
